import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Container,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import quotesApi from '../../api/quotesApi';
import useCart from '../../cart/useCart';
import ProductCard from '../../sections/client/products/ProductCard';
import { categoryIndentSx, flattenCategoryTree } from '../../utils/categoryTree';

export default function CatalogPage() {
  const { addItem, openDrawer } = useCart();
  const [searchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const categoryOptions = useMemo(() => flattenCategoryTree(categories), [categories]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    quotesApi
      .getCatalogCategories()
      .then(({ data }) => setCategories(data.data))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    setSearchInput((current) => (urlSearch !== current ? urlSearch : current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    setError(false);

    const timeout = setTimeout(async () => {
      try {
        const { data } = await quotesApi.getCatalogProducts({
          search: searchInput || undefined,
          category_id: selectedCategory?.id,
          limit: 50,
        });
        setProducts(data.data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, selectedCategory]);

  const handleAddToCart = (product, quantity) => {
    addItem(product, quantity);
    openDrawer();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Stack spacing={1} sx={{ mb: 4 }}>
        <Typography variant="h4">Catálogo de productos</Typography>
        <Typography color="text.secondary">Busca y agrega productos a tu carrito de compras.</Typography>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
        <TextField
          fullWidth
          label="Buscar producto"
          placeholder="Por nombre"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Autocomplete
          sx={{ minWidth: { sm: 280 } }}
          options={categoryOptions}
          value={selectedCategory}
          onChange={(event, value) => setSelectedCategory(value)}
          getOptionLabel={(category) => category.path}
          renderOption={({ key, ...props }, category) => (
            <li key={key} {...props}>
              <Typography variant="body2" sx={{ ...categoryIndentSx(category.depth), pl: category.depth * 2.5 }}>
                {category.name}
              </Typography>
            </li>
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => <TextField {...params} label="Categoría" placeholder="Filtrar" />}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          No se pudo cargar el catálogo. Intenta nuevamente.
        </Alert>
      )}

      {!loading && !error && products.length === 0 && (
        <Alert severity="info">No encontramos productos con ese criterio.</Alert>
      )}

      <Grid container spacing={3}>
        {loading
          ? Array.from({ length: 8 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
                <Skeleton variant="rounded" height={220} />
              </Grid>
            ))
          : products.map((product) => (
              <Grid key={product.id} size={{ xs: 12, sm: 6, md: 3 }}>
                <ProductCard product={product} onAddToCart={handleAddToCart} />
              </Grid>
            ))}
      </Grid>
    </Container>
  );
}
